import type { MetaFunction } from "@remix-run/cloudflare";
import { useState, useEffect } from "react";
import {
  Briefcase, UtensilsCrossed, Package, ChevronLeft,
  ChevronUp, Receipt, CreditCard, Sparkles, Save, FileDown, Trash2, FolderOpen,
  Bookmark, Plus, Library, Pencil, Check, FileText, BarChart3,
} from "lucide-react";
import AppAuthGate, { isAppAuthenticated } from "~/components/AppAuthGate";
import { SplashScreen } from "~/components/SplashScreen";
import { HeroDisplay } from "~/components/burn-rate/HeroDisplay";
import { DonutChart } from "~/components/burn-rate/DonutChart";
import { ScenarioBars } from "~/components/burn-rate/ScenarioBars";
import { MetricRings } from "~/components/burn-rate/MetricRings";
import { WaterfallStrip } from "~/components/burn-rate/WaterfallStrip";
import { BreakEvenCard } from "~/components/burn-rate/BreakEvenCard";
import { FoodCostCard } from "~/components/burn-rate/FoodCostCard";
import { CircularButton } from "~/components/burn-rate/CircularButton";
import { InputCard } from "~/components/burn-rate/InputCard";
import { QuoteModal } from "~/components/burn-rate/QuoteModal";
import type { QuoteConfig } from "~/components/burn-rate/QuoteModal";
import { ProjectionView } from "~/components/burn-rate/ProjectionView";
import {
  COUNTRIES, WORLD_COUNTRIES, getCountry, num, makeFmt, applyRounding, computePricing,
  breakevenUnits, foodCostHealth,
  ROUNDING_LABELS, type RoundingMode, type CalcMode, type PricingSettings,
} from "~/lib/burn-rate";
import {
  localStore, localInsumoStore,
  type SavedProject, type SavedInsumo,
} from "~/lib/burn-rate-store";
import {
  useBurnRateProfile,
  CATEGORY_LABELS, SUBCATEGORIES,
  type BurnRateProfile,
} from "~/lib/burn-rate-profile";
import styles from "~/styles/burn-rate.module.css";

export const meta: MetaFunction = () => [
  { title: "Burn Rate — Prodigium Dynamics" },
  { name: "description", content: "Calculadora de precios para independientes y negocios." },
];

// ── TYPES ─────────────────────────────────────────────────────────────────────

type Phase = "onboarding" | "calculator" | "projection";
type ControlTab = "costos" | "margen" | "impuestos" | "comisiones";
interface Expense { id: string; name: string; amount: string; }
interface Item    { id: string; name: string; unitCost: string; quantity: string; }

const uid = () => Math.random().toString(36).slice(2, 9);
const esc = (s: string) =>
  s.replace(/[&<>"'`\/]/g, (c) =>
    ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;", "`": "&#96;", "\\": "&#92;", "/": "&#47;" }[c]!));

// ── ONBOARDING CONFIG ─────────────────────────────────────────────────────────

const MODES = [
  { id: "services" as CalcMode, Icon: Briefcase,       label: "Servicios",
    tag: "Freelance · Consultoría · Diseño · Coaching",
    desc: "Calcula cuánto cobrar por hora o proyecto para cubrir tus gastos y ganar bien." },
  { id: "recipes" as CalcMode,  Icon: UtensilsCrossed, label: "Gastronomía",
    tag: "Cocina · Repostería · Bebidas · Catering",
    desc: "Costea cada ingrediente de tu receta y descubre el precio justo de tu platillo." },
  { id: "product" as CalcMode,  Icon: Package,         label: "Producto físico",
    tag: "Manufactura · Artesanías · Cosmética · Moda",
    desc: "Suma materiales, ensamblaje y overhead para conocer tu precio de venta real." },
];

// ── PROFILE BAR ───────────────────────────────────────────────────────────────

function ProfileBar({
  profile,
  update,
}: {
  profile: BurnRateProfile;
  update: (u: Partial<BurnRateProfile>) => void;
}) {
  const [editing, setEditing] = useState(false);
  const initial = profile.businessName.trim().charAt(0).toUpperCase() || "?";

  return (
    <div className={styles.profileBar}>
      {editing ? (
        <div className={styles.profileEdit}>
          <div className={styles.profileEditRow}>
            <input
              className={styles.profileEditInput}
              value={profile.businessName}
              onChange={(e) => update({ businessName: e.target.value })}
              placeholder="Nombre del negocio o solopreneur"
              maxLength={48}
            />
          </div>
          <div className={styles.profileEditRow}>
            <select
              className={styles.profileEditSelect}
              value={profile.category}
              onChange={(e) => update({ category: e.target.value as BurnRateProfile["category"] })}
            >
              {(Object.entries(CATEGORY_LABELS) as [BurnRateProfile["category"], string][]).map(([k, v]) => (
                <option key={k} value={k}>{v}</option>
              ))}
            </select>
            <select
              className={styles.profileEditSelect}
              value={profile.subcategory}
              onChange={(e) => update({ subcategory: e.target.value })}
            >
              {SUBCATEGORIES[profile.category].map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>
          <div className={styles.profileEditActions}>
            <button className={styles.profileSaveBtn} onClick={() => setEditing(false)}>
              <Check size={11} strokeWidth={2} /> Guardar
            </button>
          </div>
        </div>
      ) : (
        <div className={styles.profileView}>
          <div className={styles.profileAvatar}>{initial}</div>
          <div className={styles.profileInfo}>
            <div className={styles.profileNameRow}>
              <span className={styles.profileName}>{profile.businessName}</span>
              <span className={`${styles.planBadge} ${profile.plan === "pro" ? styles.planPro : styles.planFree}`}>
                {profile.plan.toUpperCase()}
              </span>
            </div>
            <div className={styles.profileChips}>
              <span className={styles.chip}>{CATEGORY_LABELS[profile.category]}</span>
              <span className={styles.chip}>{profile.subcategory}</span>
            </div>
          </div>
          <button className={styles.profileEditBtn} onClick={() => setEditing(true)} aria-label="Editar perfil">
            <Pencil size={12} strokeWidth={1.4} />
          </button>
        </div>
      )}
    </div>
  );
}

// ── COMPONENT ─────────────────────────────────────────────────────────────────

export default function BurnRate() {
  const [authReady, setAuthReady] = useState(() => isAppAuthenticated("BURN RATE"));

  const { profile, update: updateProfile } = useBurnRateProfile();

  const [phase, setPhase] = useState<Phase>("onboarding");
  const [mode, setMode]   = useState<CalcMode>("services");
  const [activeTab, setActiveTab] = useState<ControlTab>("costos");

  // ── Services state
  const [expenses, setExpenses] = useState<Expense[]>([
    { id: uid(), name: "Renta / oficina", amount: "" },
    { id: uid(), name: "Internet",        amount: "" },
    { id: uid(), name: "Suscripciones",   amount: "" },
  ]);
  const [hoursPerDay,  setHoursPerDay]  = useState("8");
  const [daysPerMonth, setDaysPerMonth] = useState("22");
  const [desiredSalary, setDesiredSalary] = useState("");
  const [billablePct,   setBillablePct]   = useState("70");

  // ── Recipes / Product state
  const [items, setItems] = useState<Item[]>([{ id: uid(), name: "", unitCost: "", quantity: "" }]);
  const [laborHours,    setLaborHours]    = useState("");
  const [laborRate,     setLaborRate]     = useState("");
  const [overheadPct,   setOverheadPct]   = useState("15");
  const [unitsPerBatch, setUnitsPerBatch] = useState("1");
  const [mermaPct,         setMermaPct]         = useState("0");
  const [packagingPerUnit, setPackagingPerUnit] = useState("");
  const [shippingPerUnit,  setShippingPerUnit]  = useState("");
  const [equipmentCost,    setEquipmentCost]    = useState("");
  const [equipmentUnits,   setEquipmentUnits]   = useState("");
  const [wholesaleMargin,  setWholesaleMargin]  = useState("20");
  const [fixedMonthly,     setFixedMonthly]     = useState("");

  // ── Shared pricing settings
  const [margin,  setMargin]  = useState("35");
  const [rounding, setRounding] = useState<RoundingMode>("none");
  const [countryCode, setCountryCode] = useState("MX");

  const [ivaEnabled, setIvaEnabled] = useState(true);
  const [ivaRate, setIvaRate] = useState("16");

  const [retEnabled, setRetEnabled] = useState(false);
  const [isrRet, setIsrRet] = useState("10");
  const [ivaRet, setIvaRet] = useState("10.67");

  const [commEnabled, setCommEnabled] = useState(false);
  const [paymentPct, setPaymentPct]   = useState("3.5");
  const [paymentFixed, setPaymentFixed] = useState("0");
  const [platformPct, setPlatformPct] = useState("0");

  // ── UI state
  const [sheetOpen, setSheetOpen] = useState(false);
  const [othersOpen, setOthersOpen] = useState(false);
  const [countrySearch, setCountrySearch] = useState("");
  const [quoteOpen, setQuoteOpen] = useState(false);

  // ── Saved projects
  const [projects, setProjects] = useState<SavedProject[]>([]);
  const [projectName, setProjectName] = useState("");
  const [currentId, setCurrentId] = useState<string | undefined>(undefined);

  // ── Insumos library
  const [insumos, setInsumos] = useState<SavedInsumo[]>([]);

  useEffect(() => {
    localStore.list().then(setProjects);
    localInsumoStore.list().then(setInsumos);
  }, []);

  // ── Country / formatting
  const country = getCountry(countryCode);
  const sym = country.symbol;
  const fmt = makeFmt(country.locale);
  const isFeatured = COUNTRIES.some((c) => c.code === countryCode);

  const applyCountry = (code: string) => {
    const c = getCountry(code);
    setCountryCode(code);
    setIvaRate(String(c.iva));
    setIsrRet(String(c.isrRet));
    setIvaRet(String(c.ivaRet));
    setIvaEnabled(c.iva > 0);
  };

  // ── Pricing settings object
  const settings: PricingSettings = {
    margin: num(margin),
    rounding,
    commissionsEnabled: commEnabled,
    paymentPct: num(paymentPct),
    paymentFixed: num(paymentFixed),
    platformPct: num(platformPct),
    ivaEnabled,
    ivaRate: num(ivaRate),
    retentionsEnabled: retEnabled,
    isrRet: num(isrRet),
    ivaRet: num(ivaRet),
  };

  // ── Services calculations
  const totalMonthly  = expenses.reduce((s, e) => s + num(e.amount), 0);
  const totalHours    = num(hoursPerDay) * num(daysPerMonth);
  const billableHours = totalHours * (num(billablePct) / 100);
  const monthlyTarget = totalMonthly + num(desiredSalary);
  const costPerHour   = billableHours > 0 ? monthlyTarget / billableHours : 0;
  const sR = computePricing(costPerHour, settings);
  const breakevenHours = sR.netPrice > 0 ? totalMonthly / sR.netPrice : 0;

  // ── Recipes / Product calculations
  const rawCost        = items.reduce((s, i) => s + num(i.unitCost) * num(i.quantity), 0);
  const rawEffective   = rawCost * (1 + num(mermaPct) / 100);
  const laborCost      = num(laborHours) * num(laborRate);
  const batchCost      = rawEffective + laborCost;
  const costPerUnitRaw = num(unitsPerBatch) > 0 ? batchCost / num(unitsPerBatch) : 0;
  const deprPerUnit    = num(equipmentUnits) > 0 ? num(equipmentCost) / num(equipmentUnits) : 0;
  const directPerUnit  = costPerUnitRaw + num(packagingPerUnit) + num(shippingPerUnit) + deprPerUnit;
  const overheadCost   = directPerUnit * (num(overheadPct) / 100);
  const totalCostUnit  = directPerUnit + overheadCost;
  const pR = computePricing(totalCostUnit, settings);

  // ── Derived metrics
  const foodCostPct  = pR.netPrice > 0 ? (totalCostUnit / pR.netPrice) * 100 : 0;
  const fcHealth     = foodCostHealth(foodCostPct);
  const wholesale    = num(wholesaleMargin) < 100 ? totalCostUnit / (1 - num(wholesaleMargin) / 100) : 0;
  const beUnits      = breakevenUnits(num(fixedMonthly), pR.netPrice, totalCostUnit);

  // ── Active result by mode
  const isServices = mode === "services";
  const isRecipe   = mode === "recipes";
  const R = isServices ? sR : pR;
  const baseCost = isServices ? costPerHour : totalCostUnit;
  const primaryLabel = isServices ? "Mínimo / hora" : "Precio mínimo / unidad";

  // ── Scenario comparator
  const baseMargin = num(margin);
  const scenarios = [...new Set([Math.max(0, baseMargin - 10), baseMargin, Math.min(95, baseMargin + 10)])]
    .map((m) => ({ margin: m, price: m < 100 ? applyRounding(baseCost / (1 - m / 100), rounding) : 0, active: m === baseMargin }));

  // ── Handlers
  const addExpense    = () => setExpenses((p) => [...p, { id: uid(), name: "", amount: "" }]);
  const removeExpense = (id: string) => setExpenses((p) => p.filter((e) => e.id !== id));
  const updateExpense = (id: string, f: "name" | "amount", v: string) =>
    setExpenses((p) => p.map((e) => (e.id === id ? { ...e, [f]: v } : e)));

  const addItem    = () => setItems((p) => [...p, { id: uid(), name: "", unitCost: "", quantity: "" }]);
  const removeItem = (id: string) => setItems((p) => p.filter((i) => i.id !== id));
  const updateItem = (id: string, f: keyof Omit<Item, "id">, v: string) =>
    setItems((p) => p.map((i) => (i.id === id ? { ...i, [f]: v } : i)));

  const enterCalc = (m: CalcMode) => { setMode(m); setPhase("calculator"); };
  const currentMode = MODES.find((m) => m.id === mode)!;

  // ── Saved projects
  const snapshot = (): Record<string, unknown> => ({
    expenses, hoursPerDay, daysPerMonth, desiredSalary, billablePct,
    items, laborHours, laborRate, overheadPct, unitsPerBatch,
    mermaPct, packagingPerUnit, shippingPerUnit, equipmentCost, equipmentUnits, wholesaleMargin, fixedMonthly,
    margin, rounding, countryCode,
    ivaEnabled, ivaRate, retEnabled, isrRet, ivaRet,
    commEnabled, paymentPct, paymentFixed, platformPct,
  });

  /* eslint-disable @typescript-eslint/no-explicit-any */
  const restore = (s: Record<string, any>) => {
    setExpenses(s.expenses ?? []); setHoursPerDay(s.hoursPerDay ?? "8"); setDaysPerMonth(s.daysPerMonth ?? "22");
    setDesiredSalary(s.desiredSalary ?? ""); setBillablePct(s.billablePct ?? "70");
    setItems(s.items ?? [{ id: uid(), name: "", unitCost: "", quantity: "" }]);
    setLaborHours(s.laborHours ?? ""); setLaborRate(s.laborRate ?? "");
    setOverheadPct(s.overheadPct ?? "15"); setUnitsPerBatch(s.unitsPerBatch ?? "1");
    setMermaPct(s.mermaPct ?? "0"); setPackagingPerUnit(s.packagingPerUnit ?? "");
    setShippingPerUnit(s.shippingPerUnit ?? ""); setEquipmentCost(s.equipmentCost ?? "");
    setEquipmentUnits(s.equipmentUnits ?? ""); setWholesaleMargin(s.wholesaleMargin ?? "20");
    setFixedMonthly(s.fixedMonthly ?? "");
    setMargin(s.margin ?? "35"); setRounding(s.rounding ?? "none"); setCountryCode(s.countryCode ?? "MX");
    setIvaEnabled(s.ivaEnabled ?? true); setIvaRate(s.ivaRate ?? "16");
    setRetEnabled(s.retEnabled ?? false); setIsrRet(s.isrRet ?? "10"); setIvaRet(s.ivaRet ?? "10.67");
    setCommEnabled(s.commEnabled ?? false); setPaymentPct(s.paymentPct ?? "3.5");
    setPaymentFixed(s.paymentFixed ?? "0"); setPlatformPct(s.platformPct ?? "0");
  };
  /* eslint-enable @typescript-eslint/no-explicit-any */

  const handleSave = async () => {
    const name = projectName.trim() || `${currentMode.label} · ${new Date().toLocaleDateString(country.locale)}`;
    const saved = await localStore.save({ id: currentId, name, mode, snapshot: snapshot() });
    setCurrentId(saved.id);
    setProjectName(saved.name);
    setProjects(await localStore.list());
  };

  const handleLoad = (p: SavedProject) => {
    setMode(p.mode);
    restore(p.snapshot);
    setCurrentId(p.id);
    setProjectName(p.name);
  };

  const handleDelete = async (id: string) => {
    await localStore.remove(id);
    if (currentId === id) { setCurrentId(undefined); setProjectName(""); }
    setProjects(await localStore.list());
  };

  // ── Insumos
  const saveInsumo = async (it: Item) => {
    const saved = await localInsumoStore.save({ name: it.name, unitCost: it.unitCost });
    if (saved) setInsumos(await localInsumoStore.list());
  };

  const addInsumoToItems = (ins: SavedInsumo) => {
    setItems((p) => {
      const empty = p.find((i) => !i.name && !i.unitCost);
      if (empty) return p.map((i) => i.id === empty.id
        ? { ...i, name: ins.name, unitCost: ins.unitCost, quantity: i.quantity || "1" } : i);
      return [...p, { id: uid(), name: ins.name, unitCost: ins.unitCost, quantity: "1" }];
    });
  };

  const deleteInsumo = async (id: string) => {
    await localInsumoStore.remove(id);
    setInsumos(await localInsumoStore.list());
  };

  // ── Export / Quote generator
  const buildQuote = (cfg: QuoteConfig) => {
    const title = projectName.trim() || `${currentMode.label}`;
    const dateStr = new Date().toLocaleDateString(country.locale, { year: "numeric", month: "long", day: "numeric" });

    // Theme styles
    const themes: Record<QuoteConfig["theme"], string> = {
      dark: `
        body { background:#0a0a14; color:#fff; }
        .card { background:#121214; border:1px solid rgba(255,255,255,0.08); }
        .accent { color:#fff; }
        .muted { color:#8E8E93; }
        .border { border-color:rgba(255,255,255,0.1); }
        .totalBox { background:#1C1C1E; }
      `,
      light: `
        body { background:#fff; color:#111; }
        .card { background:#f8f9fa; border:1px solid #e5e5e5; }
        .accent { color:#2A3EF4; }
        .muted { color:#666; }
        .border { border-color:#ddd; }
        .totalBox { background:#111; color:#fff; }
      `,
      minimal: `
        body { background:#fff; color:#000; }
        .card { background:#fff; border:1px solid #000; }
        .accent { color:#000; }
        .muted { color:#555; }
        .border { border-color:#000; }
        .totalBox { background:#000; color:#fff; }
      `,
      corporate: `
        body { background:#f5f6f8; color:#1a1a1a; }
        .card { background:#fff; border:1px solid #d1d5db; }
        .accent { color:#1e40af; }
        .muted { color:#6b7280; }
        .border { border-color:#d1d5db; }
        .totalBox { background:#1e3a5f; color:#fff; }
      `,
    };

    const t = themes[cfg.theme];

    // Sections
    const clientBlock = cfg.clientName
      ? `<div class="card" style="padding:16px 18px; border-radius:14px; margin-bottom:18px;">
           <p class="muted" style="font-size:9px; letter-spacing:.2em; text-transform:uppercase; margin:0 0 4px;">Para</p>
           <p style="font-size:15px; font-weight:700; margin:0;">${esc(cfg.clientName)}</p>
           ${cfg.projectDescription ? `<p style="font-size:11px; margin:4px 0 0; opacity:.7;">${esc(cfg.projectDescription)}</p>` : ""}
         </div>`
      : "";

    const lines = isServices
      ? expenses.filter((e) => num(e.amount) > 0)
          .map((e) => `<tr><td>${esc(e.name || "Gasto")}</td><td>${sym}${fmt(num(e.amount))}</td></tr>`).join("")
      : items.filter((i) => num(i.unitCost) > 0)
          .map((i) => `<tr><td>${esc(i.name || "Insumo")} <span style="font-size:10px; opacity:.6;">×${esc(i.quantity || "1")}</span></td><td>${sym}${fmt(num(i.unitCost) * num(i.quantity))}</td></tr>`).join("");

    const row = (l: string, v: string) => `<tr><td>${l}</td><td>${v}</td></tr>`;
    const breakdownRows = [
      cfg.includeBreakdown ? row(isServices ? "Costo por hora" : "Costo total por unidad", `${sym}${fmt(baseCost)}`) : "",
      cfg.includeBreakdown ? row(`Ganancia (${num(margin)}%)`, `${sym}${fmt(R.profit)}`) : "",
      cfg.includeBreakdown ? row("Precio neto", `${sym}${fmt(R.netPrice)}`) : "",
      cfg.includeBreakdown && commEnabled && R.commissionTotal > 0 ? row("Comisiones", `${sym}${fmt(R.commissionTotal)}`) : "",
      cfg.includeBreakdown && ivaEnabled && R.ivaAmount > 0 ? row(`IVA (${num(ivaRate)}%)`, `${sym}${fmt(R.ivaAmount)}`) : "",
    ].join("");

    const summaryBlock = cfg.includeSummary
      ? `<div class="totalBox" style="padding:22px 20px; border-radius:16px; display:flex; justify-content:space-between; align-items:center; margin-top:22px;">
           <div>
             <p style="font-size:9px; letter-spacing:.2em; text-transform:uppercase; opacity:.7; margin:0 0 4px;">${esc(primaryLabel)}</p>
             ${ivaEnabled ? `<p style="font-size:10px; margin:0; opacity:.7;">Cliente paga ${sym}${fmt(R.clientPays)}</p>` : ""}
           </div>
           <p style="font-size:32px; font-weight:700; margin:0; letter-spacing:-.02em;">${sym}${fmt(R.netPrice)}</p>
         </div>`
      : "";

    const chartsBlock = cfg.includeCharts
      ? `<div class="card" style="padding:14px 16px; border-radius:14px; margin-top:14px;">
           <p class="muted" style="font-size:9px; letter-spacing:.2em; text-transform:uppercase; margin:0 0 10px;">Métricas clave</p>
           <div style="display:grid; grid-template-columns:repeat(3,1fr); gap:10px;">
             <div style="text-align:center;">
               <p style="font-size:18px; font-weight:700; margin:0;">${R.markupPct.toFixed(0)}%</p>
               <p class="muted" style="font-size:8px; letter-spacing:.1em; text-transform:uppercase; margin:4px 0 0;">Markup</p>
             </div>
             <div style="text-align:center;">
               <p style="font-size:18px; font-weight:700; margin:0;">${R.marginPct.toFixed(0)}%</p>
               <p class="muted" style="font-size:8px; letter-spacing:.1em; text-transform:uppercase; margin:4px 0 0;">Margen</p>
             </div>
             <div style="text-align:center;">
               <p style="font-size:18px; font-weight:700; margin:0;">${sym}${fmt(R.profit)}</p>
               <p class="muted" style="font-size:8px; letter-spacing:.1em; text-transform:uppercase; margin:4px 0 0;">Ganancia</p>
             </div>
           </div>
         </div>`
      : "";

    const scenariosBlock = cfg.includeScenarios && scenarios.length > 1
      ? `<div style="margin-top:14px;">
           <p class="muted" style="font-size:9px; letter-spacing:.2em; text-transform:uppercase; margin:0 0 8px;">Escenarios de margen</p>
           <div style="display:grid; grid-template-columns:repeat(3,1fr); gap:8px;">
             ${scenarios.map((s) => `<div class="card" style="padding:10px; border-radius:12px; text-align:center;">
               <p style="font-size:10px; margin:0 0 4px; opacity:.7;">${s.margin}%</p>
               <p style="font-size:14px; font-weight:700; margin:0;">${sym}${fmt(s.price)}</p>
             </div>`).join("")}
           </div>
         </div>`
      : "";

    const breakevenBlock = cfg.includeBreakeven
      ? (isServices && breakevenHours > 0)
        ? `<p class="muted" style="font-size:10px; line-height:1.5; margin-top:14px; padding:10px 12px; border:1px dashed; border-radius:10px;" class="border">
             Punto de equilibrio: <strong>${fmt(breakevenHours)} h/mes</strong> facturadas cubren tus gastos fijos.
           </p>`
        : (!isServices && beUnits > 0)
          ? `<p class="muted" style="font-size:10px; line-height:1.5; margin-top:14px; padding:10px 12px; border:1px dashed; border-radius:10px;" class="border">
               Punto de equilibrio: <strong>${Math.ceil(beUnits)} unidades/mes</strong> para cubrir tus gastos fijos.
             </p>`
          : ""
      : "";

    const notesBlock = cfg.includeNotes
      ? `<p style="font-size:8px; opacity:.5; line-height:1.6; margin-top:28px; text-align:center; letter-spacing:.02em;">
           Precio referencial calculado con Burn Rate de Prodigium Dynamics. Verifica tasas e impuestos vigentes de tu país. No constituye una factura fiscal.
         </p>`
      : "";

    const html = `<!doctype html><html lang="es"><head><meta charset="utf-8">
<title>Cotización — ${esc(title)}</title>
<style>
  * { margin:0; padding:0; box-sizing:border-box; }
  body { font-family: 'Segoe UI', system-ui, -apple-system, sans-serif; padding:36px 28px; max-width:680px; margin:0 auto; line-height:1.5; }
  ${t}
  .tag { font-size:9px; letter-spacing:.3em; text-transform:uppercase; margin-bottom:6px; }
  h1 { font-size:24px; font-weight:700; letter-spacing:-.01em; margin-bottom:4px; }
  .sub { font-size:11px; margin-bottom:22px; opacity:.7; }
  .meta { display:flex; justify-content:space-between; font-size:10px; opacity:.7; border-top:1px solid; border-bottom:1px solid; padding:10px 0; margin-bottom:20px; }
  h2 { font-size:9px; letter-spacing:.2em; text-transform:uppercase; margin:20px 0 8px; opacity:.7; }
  table { width:100%; border-collapse:collapse; font-size:11px; }
  td { padding:7px 0; border-bottom:1px solid; opacity:.8; }
  td:last-child { text-align:right; white-space:nowrap; font-weight:600; }
  @media print { body { padding:24px; } }
</style></head>
<body>
  <p class="tag accent">Prodigium Dynamics · Burn Rate</p>
  <h1>${esc(title)}</h1>
  <p class="sub">Cotización generada con la calculadora de precios — modo ${esc(currentMode.label)}.</p>
  <div class="meta border"><span>${dateStr}</span><span>${esc(country.label)} · ${esc(country.currency)}</span></div>
  ${clientBlock}
  ${cfg.includeBreakdown ? `<h2>${isServices ? "Gastos considerados" : "Insumos / materiales"}</h2><table>${lines || `<tr><td>—</td><td>—</td></tr>`}</table>` : ""}
  ${cfg.includeBreakdown && breakdownRows ? `<h2>Desglose de precio</h2><table>${breakdownRows}</table>` : ""}
  ${summaryBlock}
  ${chartsBlock}
  ${scenariosBlock}
  ${breakevenBlock}
  ${notesBlock}
</body></html>`;

    const w = window.open("", "_blank", "width=800,height=900");
    if (!w) return;
    w.document.write(html);
    w.document.close();
    w.focus();
    setTimeout(() => w.print(), 400);
  };

  const printQuote = () => buildQuote({
    clientName: "",
    projectDescription: "",
    includeSummary: true,
    includeBreakdown: true,
    includeCharts: false,
    includeScenarios: false,
    includeBreakeven: true,
    includeNotes: true,
    theme: "dark",
  });

  // ── Labels
  const itemPlaceholder = isRecipe ? "Harina, azúcar, empaque..." : "Tornillos, tela, envase...";
  const addItemLabel    = isRecipe ? "+ Agregar ingrediente" : "+ Agregar material";
  const laborLabel      = isRecipe ? "Horas de preparación" : "Horas de producción";
  const batchLabel      = isRecipe ? "Porciones / unidades" : "Unidades por lote";

  // ── Dashboard data
  const donutSegments = [
    { label: "Costo", value: R.baseCost, color: "#636366" },
    { label: "Ganancia", value: R.profit, color: "#FFFFFF" },
    { label: "Comisiones", value: R.commissionTotal, color: "#8E8E93" },
    { label: "IVA", value: R.ivaAmount, color: "#48484A" },
  ];

  const waterfallItems = [
    { label: "Costo", value: R.baseCost, color: "#636366" },
    { label: "Ganancia", value: R.profit, color: "#FFFFFF" },
    ...(R.commissionTotal > 0 ? [{ label: "Comisiones", value: R.commissionTotal, color: "#8E8E93" }] : []),
    ...(R.ivaAmount > 0 ? [{ label: "IVA", value: R.ivaAmount, color: "#48484A" }] : []),
  ];

  const metricRings = [
    { label: "Markup", value: R.markupPct, max: 200, unit: "%" },
    { label: "Margen", value: R.marginPct, max: 100, unit: "%" },
    ...(isServices ? [{ label: "Utilización", value: num(billablePct), max: 100, unit: "%" }] : []),
    ...(isRecipe ? [{ label: "Food Cost", value: foodCostPct, max: 100, unit: "%", color: fcHealth === "good" ? "#34C759" : fcHealth === "warn" ? "#FF9500" : "#FF3B30" }] : []),
  ];

  // Auth gate — must be after all hooks
  if (!authReady) {
    return <AppAuthGate appName="BURN RATE" appTag="Calculadora de Precios" onAuth={() => setAuthReady(true)} />;
  }

  // ══════════════════════════════════════════════════════════════ ONBOARDING
  if (phase === "onboarding") {
    return (
      <>
        <SplashScreen name="BURN RATE" tag="Calculadora de Precios" />
        <div className={styles.onboarding}>
          <div className={styles.obInner}>
            <p className={styles.obTag}>PRODIGIUM_OS · HERRAMIENTA</p>
            <h1 className={styles.obTitle}>BURN RATE</h1>
            <p className={styles.obSub}>
              Antes de calcular, cuéntanos sobre tu negocio para recomendarte el modo correcto.
            </p>
            <p className={styles.obQuestion}>¿Cuál es tu caso?</p>
            <div className={styles.obCards}>
              {MODES.map(({ id, Icon, label, tag, desc }) => (
                <button key={id} className={styles.obCard} onClick={() => enterCalc(id)}>
                  <div className={styles.obCardIcon}><Icon size={20} strokeWidth={1.2} /></div>
                  <div className={styles.obCardBody}>
                    <p className={styles.obCardLabel}>{label}</p>
                    <p className={styles.obCardTag}>{tag}</p>
                    <p className={styles.obCardDesc}>{desc}</p>
                  </div>
                  <span className={styles.obCardArrow}>→</span>
                </button>
              ))}
            </div>
            <p className={styles.obFooter}>
              Puedes cambiar de modo en cualquier momento dentro de la calculadora.
            </p>
          </div>
        </div>
      </>
    );
  }

  // ══════════════════════════════════════════════════════════════ PROJECTION
  if (phase === "projection") {
    return (
      <ProjectionView
        projects={projects}
        monthlyFixed={totalMonthly}
        sym={sym}
        fmt={fmt}
        onClose={() => setPhase("calculator")}
      />
    );
  }

  // ══════════════════════════════════════════════════════════════ CALCULATOR
  return (
    <>

      <div className={styles.root}>

        {/* Header */}
        <div className={styles.desktopTopArea}>
        <header className={styles.header}>
          <CircularButton size="sm" onClick={() => setPhase("onboarding")} aria-label="Cambiar modo">
            <ChevronLeft size={14} strokeWidth={1.5} />
          </CircularButton>
          <span className={styles.headerTag}>{currentMode.label.toUpperCase()}</span>
          <CircularButton size="sm" onClick={() => setPhase("projection")} aria-label="Proyección"><BarChart3 size={14} strokeWidth={1.5} /></CircularButton>
        </header>

        <ProfileBar profile={profile} update={updateProfile} />
        </div>

        <div className={styles.desktopSidebarArea}>
        <ExchangeRates />

        {/* Mode selector */}
        <div className={styles.modeBar}>
          {MODES.map(({ id, Icon, label }) => (
            <button key={id}
              className={`${styles.modeBtn} ${mode === id ? styles.modeBtnActive : ""}`}
              onClick={() => setMode(id)}>
              <Icon size={12} strokeWidth={1.4} /> {label}
            </button>
          ))}
        </div>
        {/* Duplicado numerado (1–3) del selector de modo, justo debajo */}
        <div className={styles.modeBar}>
          {MODES.map(({ id, Icon, label }, i) => (
            <button key={`num-${id}`}
              className={`${styles.modeBtn} ${mode === id ? styles.modeBtnActive : ""}`}
              onClick={() => setMode(id)}>
              {i + 1} <Icon size={12} strokeWidth={1.4} /> {label}
            </button>
          ))}
        </div>
        </div>

        <div className={styles.desktopMainArea}>
        {/* Hero Display */}
        <HeroDisplay
          primaryLabel={primaryLabel}
          netPrice={fmt(R.netPrice)}
          clientPays={fmt(R.clientPays)}
          youReceive={fmt(R.youReceive)}
          profit={fmt(R.profit)}
          sym={sym}
        />

        {/* Dashboard scroll */}
        <div className={styles.dashboardScroll}>
          <DonutChart segments={donutSegments} sym={sym} total={R.clientPays} />
          <ScenarioBars scenarios={scenarios} sym={sym} />
          <MetricRings rings={metricRings} />
          <WaterfallStrip items={waterfallItems} sym={sym} />
          {isServices && breakevenHours > 0 && (
            <BreakEvenCard value={`${fmt(breakevenHours)} h`} label="/ mes para cubrir gastos" isServices />
          )}
          {!isServices && beUnits > 0 && (
            <BreakEvenCard value={`${Math.ceil(beUnits)}`} label="unidades / mes para cubrir gastos" isServices={false} />
          )}
          {isRecipe && foodCostPct > 0 && (
            <FoodCostCard pct={foodCostPct} health={fcHealth} />
          )}
          <button className={styles.quoteCard} onClick={() => setQuoteOpen(true)} aria-label="Generar cotización">
            <div className={styles.quoteCardIcon}>
              <FileText size={18} strokeWidth={1.4} />
            </div>
            <span className={styles.quoteCardLabel}>Generar<br />cotización</span>
          </button>
        </div>

        {/* Mis cálculos — ahora encima de las tabs */}
        <div className={styles.section}>
          <p className={styles.sectionTitle}>Mis cálculos</p>
          <div className={styles.saveRow}>
            <div className={styles.inputCardInner}>
              <input
                className={styles.inputCardField}
                placeholder="Nombre del cálculo"
                value={projectName}
                onChange={(e) => setProjectName(e.target.value)}
              />
            </div>
            <button className={styles.saveBtn} onClick={handleSave}>
              <Save size={12} strokeWidth={1.5} /> {currentId ? "Actualizar" : "Guardar"}
            </button>
          </div>
          {projects.length === 0 ? (
            <p className={styles.sectionHint}>Aún no tienes cálculos guardados. Se guardan en este dispositivo.</p>
          ) : (
            <div className={styles.projList}>
              {projects.map((p) => (
                <div key={p.id} className={`${styles.projItem} ${currentId === p.id ? styles.projItemActive : ""}`}>
                  <button className={styles.projLoad} onClick={() => handleLoad(p)}>
                    <span className={styles.projName}>{p.name}</span>
                    <span className={styles.projMeta}>{MODES.find((m) => m.id === p.mode)?.label}</span>
                  </button>
                  <button className={styles.projDel} onClick={() => handleDelete(p.id)} aria-label="Eliminar">
                    <Trash2 size={12} strokeWidth={1.5} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Control Tabs */}
        <div className={styles.controlTabs}>
          {[
            { key: "costos" as ControlTab, label: "Costos" },
            { key: "margen" as ControlTab, label: "Margen" },
            { key: "impuestos" as ControlTab, label: "Impuestos" },
            { key: "comisiones" as ControlTab, label: "Comisiones" },
          ].map((t) => (
            <button
              key={t.key}
              className={`${styles.controlTab} ${activeTab === t.key ? styles.controlTabActive : ""}`}
              onClick={() => setActiveTab(t.key)}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Control Panel */}
        <div className={styles.panel}>

          {/* ════════════════════════ TAB: COSTOS */}
          {activeTab === "costos" && (
            <>
              {isServices && (
                <div className={styles.section}>
                  <p className={styles.sectionTitle}>Gastos fijos mensuales</p>
                  <p className={styles.sectionHint}>Todo lo que pagas aunque no trabajes.</p>
                  <div className={styles.list}>
                    {expenses.map((e) => (
                      <div key={e.id} className={styles.row}>
                        <div className={styles.inputCardInner}>
                          <input
                            className={styles.inputCardField}
                            placeholder="Concepto"
                            value={e.name}
                            onChange={(ev) => updateExpense(e.id, "name", ev.target.value)}
                          />
                        </div>
                        <div className={styles.inputCardInner}>
                          <span className={styles.inputCardPrefix}>{sym}</span>
                          <input
                            className={styles.inputCardField}
                            placeholder="0.00"
                            type="number"
                            inputMode="decimal"
                            min="0"
                            value={e.amount}
                            onChange={(ev) => updateExpense(e.id, "amount", ev.target.value)}
                          />
                        </div>
                        <CircularButton size="sm" variant="danger" onClick={() => removeExpense(e.id)}>✕</CircularButton>
                      </div>
                    ))}
                  </div>
                  <button className={styles.addBtn} onClick={addExpense}>+ Agregar gasto</button>
                </div>
              )}

              {!isServices && (
                <div className={styles.section}>
                  <p className={styles.sectionTitle}>{isRecipe ? "Ingredientes" : "Materiales"}</p>
                  <p className={styles.sectionHint}>
                    {isRecipe
                      ? "Agrega cada ingrediente con su costo unitario y cantidad."
                      : "Lista todos los materiales necesarios."}
                  </p>
                  <div className={styles.list}>
                    {items.map((i) => (
                      <div key={i.id} className={styles.itemRow}>
                        <div className={styles.itemTop}>
                          <div className={styles.inputCardInner} style={{ flex: 1 }}>
                            <input
                              className={styles.inputCardField}
                              placeholder={itemPlaceholder}
                              value={i.name}
                              onChange={(ev) => updateItem(i.id, "name", ev.target.value)}
                            />
                          </div>
                          <button className={styles.iconBtn} onClick={() => saveInsumo(i)} aria-label="Guardar en biblioteca">
                            <Bookmark size={12} strokeWidth={1.5} />
                          </button>
                          <CircularButton size="sm" variant="danger" onClick={() => removeItem(i.id)}>✕</CircularButton>
                        </div>
                        <div className={styles.itemBottom}>
                          <label className={styles.subLabel}>
                            Costo unitario
                            <div className={styles.inputCardInner}>
                              <span className={styles.inputCardPrefix}>{sym}</span>
                              <input
                                className={styles.inputCardField}
                                placeholder="0.00"
                                type="number"
                                inputMode="decimal"
                                min="0"
                                value={i.unitCost}
                                onChange={(ev) => updateItem(i.id, "unitCost", ev.target.value)}
                              />
                            </div>
                          </label>
                          <label className={styles.subLabel}>
                            Cantidad
                            <div className={styles.inputCardInner}>
                              <input
                                className={styles.inputCardField}
                                placeholder="1"
                                type="number"
                                inputMode="decimal"
                                min="0"
                                value={i.quantity}
                                onChange={(ev) => updateItem(i.id, "quantity", ev.target.value)}
                              />
                            </div>
                          </label>
                          <div className={styles.itemSubtotal}>
                            <span className={styles.subLabel}>Subtotal</span>
                            <span className={styles.subValue}>{sym}{fmt(num(i.unitCost) * num(i.quantity))}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  <button className={styles.addBtn} onClick={addItem}>{addItemLabel}</button>

                  {/* Biblioteca de insumos */}
                  {insumos.length > 0 && (
                    <div style={{ marginTop: 14 }}>
                      <p className={styles.sectionTitle}>Biblioteca ({insumos.length})</p>
                      <div className={styles.libList}>
                        {insumos.map((ins) => (
                          <div key={ins.id} className={styles.libItem}>
                            <button className={styles.libAdd} onClick={() => addInsumoToItems(ins)}>
                              <Plus size={11} strokeWidth={1.6} />
                              <span className={styles.libName}>{ins.name}</span>
                              <span className={styles.libCost}>{sym}{fmt(num(ins.unitCost))}</span>
                            </button>
                            <button className={styles.projDel} onClick={() => deleteInsumo(ins.id)} aria-label="Eliminar">
                              <Trash2 size={12} strokeWidth={1.5} />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {!isServices && (
                <div className={styles.section}>
                  <p className={styles.sectionTitle}>Producción</p>
                  <div className={styles.fieldGrid}>
                    <InputCard
                      label={laborLabel}
                      type="number"
                      inputMode="decimal"
                      min="0"
                      placeholder="0"
                      value={laborHours}
                      onChange={(e) => setLaborHours(e.target.value)}
                    />
                    <InputCard
                      label={`Costo / hora (${sym})`}
                      type="number"
                      inputMode="decimal"
                      min="0"
                      placeholder="0.00"
                      value={laborRate}
                      onChange={(e) => setLaborRate(e.target.value)}
                    />
                    <InputCard
                      label={batchLabel}
                      type="number"
                      inputMode="decimal"
                      min="1"
                      value={unitsPerBatch}
                      onChange={(e) => setUnitsPerBatch(e.target.value)}
                    />
                    <InputCard
                      label="Gastos indirectos %"
                      type="number"
                      inputMode="decimal"
                      min="0"
                      max="99"
                      value={overheadPct}
                      onChange={(e) => setOverheadPct(e.target.value)}
                    />
                  </div>
                </div>
              )}

              {isServices && (
                <div className={styles.section}>
                  <p className={styles.sectionTitle}>Capacidad de trabajo</p>
                  <div className={styles.fieldGrid}>
                    <InputCard
                      label="Horas por día"
                      type="number"
                      inputMode="decimal"
                      min="1"
                      max="24"
                      value={hoursPerDay}
                      onChange={(e) => setHoursPerDay(e.target.value)}
                    />
                    <InputCard
                      label="Días por mes"
                      type="number"
                      inputMode="decimal"
                      min="1"
                      max="31"
                      value={daysPerMonth}
                      onChange={(e) => setDaysPerMonth(e.target.value)}
                    />
                    <InputCard
                      label="Sueldo deseado / mes"
                      prefix={sym}
                      type="number"
                      inputMode="decimal"
                      min="0"
                      placeholder="0"
                      value={desiredSalary}
                      onChange={(e) => setDesiredSalary(e.target.value)}
                    />
                    <InputCard
                      label="Utilización %"
                      type="number"
                      inputMode="decimal"
                      min="1"
                      max="100"
                      value={billablePct}
                      onChange={(e) => setBillablePct(e.target.value)}
                    />
                  </div>
                </div>
              )}
            </>
          )}

          {/* ════════════════════════ Detalles de costo (fusionado en Costos) */}
          {activeTab === "costos" && (
            <div className={styles.section}>
              <p className={styles.sectionTitle}>Detalles de costo</p>
              <div className={styles.fieldGrid}>
                {isRecipe && (
                  <InputCard
                    label="Merma / desperdicio %"
                    type="number"
                    inputMode="decimal"
                    min="0"
                    max="99"
                    value={mermaPct}
                    onChange={(e) => setMermaPct(e.target.value)}
                  />
                )}
                <InputCard
                  label="Empaque / unidad"
                  prefix={sym}
                  type="number"
                  inputMode="decimal"
                  min="0"
                  placeholder="0.00"
                  value={packagingPerUnit}
                  onChange={(e) => setPackagingPerUnit(e.target.value)}
                />
                {!isRecipe && (
                  <>
                    <InputCard
                      label="Envío / unidad"
                      prefix={sym}
                      type="number"
                      inputMode="decimal"
                      min="0"
                      placeholder="0.00"
                      value={shippingPerUnit}
                      onChange={(e) => setShippingPerUnit(e.target.value)}
                    />
                    <InputCard
                      label="Costo de equipo"
                      prefix={sym}
                      type="number"
                      inputMode="decimal"
                      min="0"
                      placeholder="0.00"
                      value={equipmentCost}
                      onChange={(e) => setEquipmentCost(e.target.value)}
                    />
                    <InputCard
                      label="Vida útil (unidades)"
                      type="number"
                      inputMode="decimal"
                      min="0"
                      placeholder="0"
                      value={equipmentUnits}
                      onChange={(e) => setEquipmentUnits(e.target.value)}
                    />
                    <InputCard
                      label="Margen mayoreo %"
                      type="number"
                      inputMode="decimal"
                      min="0"
                      max="99"
                      value={wholesaleMargin}
                      onChange={(e) => setWholesaleMargin(e.target.value)}
                    />
                  </>
                )}
                <InputCard
                  label="Gastos fijos / mes"
                  prefix={sym}
                  type="number"
                  inputMode="decimal"
                  min="0"
                  placeholder="0"
                  value={fixedMonthly}
                  onChange={(e) => setFixedMonthly(e.target.value)}
                />
              </div>
            </div>
          )}

          {/* ════════════════════════ TAB: MARGEN */}
          {activeTab === "margen" && (
            <div className={styles.section}>
              <p className={styles.sectionTitle}>Margen y precio</p>
              <div className={styles.fieldGrid}>
                <InputCard
                  label="Margen de ganancia %"
                  type="number"
                  inputMode="decimal"
                  min="0"
                  max="99"
                  value={margin}
                  onChange={(e) => setMargin(e.target.value)}
                />
              </div>
              <p className={styles.sectionHint} style={{ marginTop: 12 }}>Redondeo</p>
              <div className={styles.chips}>
                {(Object.keys(ROUNDING_LABELS) as RoundingMode[]).map((r) => (
                  <button key={r}
                    className={`${styles.chip} ${rounding === r ? styles.chipActive : ""}`}
                    onClick={() => setRounding(r)}>
                    {ROUNDING_LABELS[r]}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* ════════════════════════ TAB: IMPUESTOS */}
          {activeTab === "impuestos" && (
            <div className={styles.section}>
              <p className={styles.sectionTitle}>País / moneda</p>
              <div className={styles.chips}>
                {COUNTRIES.map((c) => (
                  <button key={c.code}
                    className={`${styles.chip} ${countryCode === c.code ? styles.chipActive : ""}`}
                    onClick={() => { applyCountry(c.code); setOthersOpen(false); }}>
                    {c.label}
                  </button>
                ))}
                <button
                  className={`${styles.chip} ${(!isFeatured || othersOpen) ? styles.chipActive : ""}`}
                  onClick={() => setOthersOpen((o) => !o)}>
                  {!isFeatured ? `${country.label} ▾` : "Otros…"}
                </button>
              </div>

              {othersOpen && (
                <div className={styles.countryPanel}>
                  <div className={styles.inputCardInner}>
                    <input
                      className={styles.inputCardField}
                      placeholder="Buscar país…"
                      value={countrySearch}
                      onChange={(e) => setCountrySearch(e.target.value)}
                    />
                  </div>
                  <div className={styles.countryList}>
                    {WORLD_COUNTRIES
                      .filter((c) => c.label.toLowerCase().includes(countrySearch.trim().toLowerCase()))
                      .map((c) => (
                        <button key={c.code}
                          className={`${styles.countryRow} ${countryCode === c.code ? styles.countryRowActive : ""}`}
                          onClick={() => { applyCountry(c.code); setOthersOpen(false); setCountrySearch(""); }}>
                          <span className={styles.countryName}>{c.label}</span>
                          <span className={styles.countryIva}>{c.currency} · IVA {c.iva}%</span>
                        </button>
                      ))}
                  </div>
                </div>
              )}

              <label className={styles.switchRow} style={{ marginTop: 14 }}>
                <span>Cobrar IVA</span>
                <input type="checkbox" className={styles.switch} checked={ivaEnabled}
                  onChange={(e) => setIvaEnabled(e.target.checked)} />
              </label>
              {ivaEnabled && (
                <InputCard
                  label="Tasa de IVA %"
                  type="number"
                  inputMode="decimal"
                  min="0"
                  max="99"
                  value={ivaRate}
                  onChange={(e) => setIvaRate(e.target.value)}
                />
              )}

              {isServices && (
                <>
                  <label className={styles.switchRow} style={{ marginTop: 14 }}>
                    <span>Retenciones (factura a empresa)</span>
                    <input type="checkbox" className={styles.switch} checked={retEnabled}
                      onChange={(e) => setRetEnabled(e.target.checked)} />
                  </label>
                  {retEnabled && (
                    <div className={styles.fieldGrid}>
                      <InputCard
                        label="Retención ISR %"
                        type="number"
                        inputMode="decimal"
                        min="0"
                        max="99"
                        value={isrRet}
                        onChange={(e) => setIsrRet(e.target.value)}
                      />
                      <InputCard
                        label="Retención IVA %"
                        type="number"
                        inputMode="decimal"
                        min="0"
                        max="99"
                        value={ivaRet}
                        onChange={(e) => setIvaRet(e.target.value)}
                      />
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* ════════════════════════ TAB: COMISIONES */}
          {activeTab === "comisiones" && (
            <div className={styles.section}>
              <p className={styles.sectionTitle}>Comisiones</p>
              <label className={styles.switchRow}>
                <span>Aplicar comisiones</span>
                <input type="checkbox" className={styles.switch} checked={commEnabled}
                  onChange={(e) => setCommEnabled(e.target.checked)} />
              </label>
              <p className={styles.sectionHint}>
                Tu precio se ajusta hacia arriba para que recibas lo que necesitas después de comisiones.
              </p>
              {commEnabled && (
                <div className={styles.fieldGrid}>
                  <InputCard
                    label="Comisión pago / tarjeta %"
                    type="number"
                    inputMode="decimal"
                    min="0"
                    max="99"
                    value={paymentPct}
                    onChange={(e) => setPaymentPct(e.target.value)}
                  />
                  <InputCard
                    label="Cargo fijo / venta"
                    prefix={sym}
                    type="number"
                    inputMode="decimal"
                    min="0"
                    value={paymentFixed}
                    onChange={(e) => setPaymentFixed(e.target.value)}
                  />
                  <InputCard
                    label="Comisión plataforma %"
                    type="number"
                    inputMode="decimal"
                    min="0"
                    max="99"
                    value={platformPct}
                    onChange={(e) => setPlatformPct(e.target.value)}
                  />
                </div>
              )}
            </div>
          )}
        </div>
        </div>
      </div>

      {/* FAB Export */}
      <button className={styles.fab} onClick={() => setSheetOpen(true)} aria-label="Exportar">
        <FileDown size={20} strokeWidth={1.5} />
      </button>

      {/* Breakdown Sheet */}
      {sheetOpen && (
        <>
          <div className={styles.sheetBackdrop} onClick={() => setSheetOpen(false)} />
          <div className={styles.sheet}>
            <div className={styles.sheetHandle} onClick={() => setSheetOpen(false)} />
            <div className={styles.sheetScroll}>
              <p className={styles.resultsTitle}>Desglose completo</p>

              <div className={styles.resultPrimary}>
                <p className={styles.resultPrimaryLabel}>{primaryLabel}</p>
                <p className={styles.resultPrimaryValue}>{sym}{fmt(R.netPrice)}</p>
              </div>

              <div className={styles.intelRow}>
                <div className={styles.intelCard}>
                  <p className={styles.intelLabel}>Markup</p>
                  <p className={styles.intelValue}>{R.markupPct.toFixed(0)}%</p>
                  <p className={styles.intelHint}>sobre costo</p>
                </div>
                <div className={styles.intelCard}>
                  <p className={styles.intelLabel}>Margen real</p>
                  <p className={styles.intelValue}>{R.marginPct.toFixed(0)}%</p>
                  <p className={styles.intelHint}>sobre precio</p>
                </div>
                <div className={styles.intelCard}>
                  <p className={styles.intelLabel}>Ganancia</p>
                  <p className={styles.intelValue}>{sym}{fmt(R.profit)}</p>
                  <p className={styles.intelHint}>por {isServices ? "hora" : "unidad"}</p>
                </div>
              </div>

              {isRecipe && foodCostPct > 0 && (
                <div className={`${styles.foodCost} ${styles[`fc_${fcHealth}`]}`}>
                  <div>
                    <p className={styles.foodCostLabel}>Food cost</p>
                    <p className={styles.foodCostHint}>
                      {fcHealth === "good" ? "Saludable — dentro del rango ideal (≤35%)."
                        : fcHealth === "warn" ? "Alto — revisa porciones o margen (35–45%)."
                        : "Crítico — estás dejando muy poca ganancia (>45%)."}
                    </p>
                  </div>
                  <p className={styles.foodCostValue}>{foodCostPct.toFixed(0)}%</p>
                </div>
              )}

              <div className={styles.breakList}>
                <Brk label={isServices ? "Costo por hora" : "Costo total por unidad"} val={`${sym}${fmt(baseCost)}`} />
                <Brk label={`Ganancia (${num(margin)}%)`} val={`${sym}${fmt(R.profit)}`} />
                <Brk label="Precio neto" val={`${sym}${fmt(R.netPrice)}`} strong />
                {commEnabled && R.commissionTotal > 0 && (
                  <>
                    <Brk label="Comisiones" val={`+ ${sym}${fmt(R.commissionTotal)}`} />
                    <Brk label="Precio a listar" val={`${sym}${fmt(R.listedPrice)}`} strong />
                  </>
                )}
                {ivaEnabled && R.ivaAmount > 0 && (
                  <Brk label={`IVA (${num(ivaRate)}%)`} val={`+ ${sym}${fmt(R.ivaAmount)}`} />
                )}
                <Brk label="Cliente paga" val={`${sym}${fmt(R.clientPays)}`} accent />
                {isServices && retEnabled && R.retentionTotal > 0 && (
                  <>
                    <Brk label="Retenciones" val={`− ${sym}${fmt(R.retentionTotal)}`} />
                    <Brk label="Tú recibes" val={`${sym}${fmt(R.youReceive)}`} strong />
                  </>
                )}
                {!isServices && !isRecipe && wholesale > 0 && (
                  <Brk label={`Precio mayoreo (${num(wholesaleMargin)}%)`} val={`${sym}${fmt(wholesale)}`} />
                )}
              </div>

              {isServices && breakevenHours > 0 && (
                <p className={styles.breakeven}>
                  Punto de equilibrio: <strong>{fmt(breakevenHours)} h/mes</strong> facturadas cubren tus gastos fijos.
                </p>
              )}
              {!isServices && beUnits > 0 && (
                <p className={styles.breakeven}>
                  Punto de equilibrio: <strong>{Math.ceil(beUnits)} unidades/mes</strong> para cubrir tus gastos fijos.
                </p>
              )}

              {scenarios.length > 1 && (
                <div className={styles.scenarios}>
                  <p className={styles.scenTitle}>Escenarios de margen</p>
                  <div className={styles.scenRow}>
                    {scenarios.map((s) => (
                      <div key={s.margin} className={`${styles.scenCard} ${s.active ? styles.scenCardActive : ""}`}>
                        <p className={styles.scenMargin}>{s.margin}%</p>
                        <p className={styles.scenPrice}>{sym}{fmt(s.price)}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className={styles.sheetActions}>
                <button className={styles.sheetBtn} onClick={handleSave}>
                  <Save size={14} strokeWidth={1.5} /> {currentId ? "Actualizar" : "Guardar"}
                </button>
                <button className={styles.sheetBtnPrimary} onClick={printQuote}>
                  <FileDown size={14} strokeWidth={1.5} /> Exportar PDF
                </button>
              </div>

              <p className={styles.resultsNote}>
                * Cálculos referenciales. Verifica tasas e impuestos vigentes de tu país.
              </p>
            </div>
          </div>
        </>
      )}

      <QuoteModal
        isOpen={quoteOpen}
        onClose={() => setQuoteOpen(false)}
        onGenerate={(cfg) => { setQuoteOpen(false); buildQuote(cfg); }}
      />
    </>
  );
}

// ── Breakdown row ─────────────────────────────────────────────────────────────
function Brk({ label, val, strong, accent }: { label: string; val: string; strong?: boolean; accent?: boolean }) {
  return (
    <div className={`${styles.brk} ${strong ? styles.brkStrong : ""} ${accent ? styles.brkAccent : ""}`}>
      <span className={styles.brkLabel}>{label}</span>
      <span className={styles.brkVal}>{val}</span>
    </div>
  );
}

// ── Live USD exchange rates ───────────────────────────────────────────────────
const FX_SHOWN = ["MXN", "COP", "ARS", "CLP", "PEN", "BRL", "EUR", "GBP"];
const FX_FALLBACK: Record<string, number> = {
  MXN: 18.5, COP: 4050, ARS: 1150, CLP: 950, PEN: 3.75, BRL: 5.5, EUR: 0.92, GBP: 0.78,
};

function fmtRate(v: number): string {
  if (!Number.isFinite(v)) return "—";
  return v >= 100
    ? Math.round(v).toLocaleString("en-US")
    : v.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function ExchangeRates() {
  const [rates, setRates] = useState<Record<string, number> | null>(null);
  const [live, setLive] = useState(false);

  useEffect(() => {
    const ctrl = new AbortController();
    fetch("https://open.er-api.com/v6/latest/USD", { signal: ctrl.signal })
      .then((r) => r.json())
      .then((d: unknown) => {
        const data = d as Record<string, unknown>;
        if (data?.result !== "success" || !data?.rates) throw new Error("bad response");
        setRates(data.rates as Record<string, number>);
        setLive(true);
      })
      .catch(() => { setRates(FX_FALLBACK); setLive(false); });
    return () => { ctrl.abort(); };
  }, []);

  const r = rates ?? FX_FALLBACK;

  return (
    <div className={styles.fx}>
      <div className={styles.fxHead}>
        <span className={styles.fxTitle}>1 USD equivale a</span>
        <span className={styles.fxStatus}>{live ? "● en vivo" : "≈ referencia"}</span>
      </div>
      <div className={styles.fxRow}>
        {FX_SHOWN.map((c) => (
          <div key={c} className={styles.fxCard}>
            <span className={styles.fxCur}>{c}</span>
            <span className={styles.fxVal}>{fmtRate(r[c])}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
